import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bookmark, FolderPlus, Check } from "lucide-react";

// Choose (or create) a saved-posts collection for a post — Facebook style.
export const SaveCollectionDialog = ({ postId, open, onOpenChange, currentCollection = null, onMoved }) => {
  const { t } = useApp();
  const [collections, setCollections] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/posts/saved/collections").then(({ data }) => setCollections(data.collections || [])).catch(() => {});
      setNewName("");
    }
  }, [open]);

  const choose = async (cid) => {
    setBusy(true);
    try {
      await api.patch(`/posts/${postId}/save`, { collection_id: cid });
      toast.success(cid ? t("collection.saved_in", "Rangé dans la collection") : t("collection.removed", "Retiré de la collection"));
      onMoved?.(cid);
      onOpenChange(false);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };

  const createAndChoose = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const { data } = await api.post("/posts/saved/collections", { name });
      await choose(data.collection.id);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} variant="sheet" className="sm:max-w-[420px]" data-testid="save-collection-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("collection.title", "Ranger dans une collection")}</DialogTitle></DialogHeader>
        <DialogBody className="space-y-1">
          <button
            onClick={() => choose(null)}
            disabled={busy}
            className="w-full flex items-center gap-2.5 p-2.5 rounded-xl text-sm text-left transition-colors hover:bg-[color:var(--yalola-surface-alt)]"
            data-testid="collection-none"
          >
            <span className="h-8 w-8 rounded-lg grid place-items-center" style={{ background: "var(--yalola-surface-alt)" }}><Bookmark size={15} /></span>
            <span className="flex-1">{t("collection.none", "Sans collection")}</span>
            {!currentCollection && <Check size={16} style={{ color: "var(--yalola-primary)" }} />}
          </button>
          {collections.map((c) => (
            <button
              key={c.id}
              onClick={() => choose(c.id)}
              disabled={busy}
              className="w-full flex items-center gap-2.5 p-2.5 rounded-xl text-sm text-left transition-colors hover:bg-[color:var(--yalola-surface-alt)]"
              data-testid={`collection-option-${c.id}`}
            >
              <span className="h-8 w-8 rounded-lg grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Bookmark size={15} /></span>
              <span className="flex-1 truncate">{c.name} <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({c.count})</span></span>
              {currentCollection === c.id && <Check size={16} style={{ color: "var(--yalola-primary)" }} />}
            </button>
          ))}
        </DialogBody>
        <DialogFooter className="flex-row items-center gap-2 sm:justify-start">
          <Input placeholder={t("collection.new_ph", "Nouvelle collection (ex. Prédications)")} value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createAndChoose()} data-testid="collection-new-name" />
          <Button className="pill-primary rounded-full shrink-0" onClick={createAndChoose} disabled={busy || !newName.trim()} data-testid="collection-create-btn">
            <FolderPlus size={15} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
